// A date-only value names a calendar day, not an instant, so it renders in UTC. Values carrying a
// time denote a real instant and keep the viewer's own timezone, so a late-evening completion shows
// the day the learner would name.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

const ABBREVIATION_LENGTH = 3

const abbreviate = (month: string) => month.slice(0, ABBREVIATION_LENGTH)

const germanMonth = (month: string) => (month.length > ABBREVIATION_LENGTH + 1 ? `${abbreviate(month)}.` : month)

/**
 * `5 Jun 2025` in English, `5. Okt. 2025` in German: day first, the month cut to three letters.
 *
 * German dots the day and the abbreviated month, and spells out `März`, `Mai`, `Juni` and `Juli`
 * without a dot.
 *
 * The parts are composed rather than left to one `Intl` pattern, which cannot produce this:
 * `en` orders the month first, and `en-GB` and German abbreviate September to four letters.
 */
export const shortDateByLocale = (value: string | null | undefined, locale: string | undefined): string | undefined => {
  if (!value) {
    return undefined
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  const timeZone = DATE_ONLY.test(value) ? { timeZone: 'UTC' as const } : {}
  const month = new Intl.DateTimeFormat(locale, { month: 'long', ...timeZone }).format(date)
  const parts = new Intl.DateTimeFormat('en-GB', { day: 'numeric', year: 'numeric', ...timeZone }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((candidate) => candidate.type === type)?.value

  if (!locale?.startsWith('de')) {
    return `${part('day')} ${abbreviate(month)} ${part('year')}`
  }

  return `${part('day')}. ${germanMonth(month)} ${part('year')}`
}
